import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { SizingToolState, ModalToggleState } from '../../models/actionStates';
import ProductFormButton from './ProductFormButton';
import { ImageLoader } from './ImageLoader';
import { SelectionType } from '../../enums/SelectionType.enum';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { assetPathFnState } from '../../recoil/atoms';
import { sizingToolState, modalState, isMobileState } from '../../recoil/selectors';
import { SubContext } from '../../enums/SubContext.enum';
import { useReverseModalState, useSetModalState } from '../../hooks/actions/useModalState';
import { useProductSelection } from '../../hooks/useProductSelection';
import { Direction, useProductAssets } from '../../hooks/useProductAssets';
import { sizeImage } from '../../util';
import { ProductSizeSwatches } from '../../components/ProductSizeSwatches';
import styles from './ProductStepper.module.scss';
import { ProductFitSwatches } from '../../components/ProductFitSwatches';
import { Button } from '../../root/components/buttons/Button';
import { LongSize } from './size.enum';
import { ModalState } from '../../models/ModalState';
import { Fit } from '../../enums/Fit.enum';

export interface ProductStepperProps {
  selectionType: SelectionType;
  selectionKey: string;
  quantity: number;
  subContextOverride?: SubContext;
  afterSubmit?: () => void;
  disablePreCartModal?: boolean;
  titleText?: string | JSX.Element;
  onNext?: (size: LongSize) => void;
  onSelect?: (size: LongSize, fit: Fit | undefined, isAvailable: boolean) => void;
  onClose?: () => void;
  hideCtaWhenDisabled?: boolean;
}

export const ProductStepper: React.FC<ProductStepperProps> = ({
  selectionType,
  selectionKey,
  subContextOverride,
  afterSubmit,
  disablePreCartModal,
  quantity,
  titleText,
  onNext,
  onSelect,
  onClose,
  hideCtaWhenDisabled,
}) => {
  const assetPathFn = useRecoilValue(assetPathFnState);

  const isMobile = useRecoilValue(isMobileState);
  const { variant, product, subContext: subContextBase, color, size } = useProductSelection(selectionType, selectionKey);
  const setSizingToolState = useSetRecoilState(sizingToolState);
  const modal = useRecoilValue(modalState);
  const reverseModal = useReverseModalState();
  const setModal = useSetModalState();
  const [originalModalState, setOriginalModalState] = useState<ModalState>(modal);

  const subContext = subContextOverride ?? subContextBase;

  useEffect(() => {
    setOriginalModalState(modal);
    // This is a hack, we basically rely on the state of the modal being stale with the original contents
    // so that when the sizing tool is closed
  }, [selectionType, selectionKey]);

  const openSizingTool = () => {
    setSizingToolState({
      sizingToolState: SizingToolState.Open,
      selectionType: selectionType,
      selectionKey: selectionKey,
      initialProduct: product,
      product: product,
      onSizeSelect: onSelect
        ? (selectedSize: LongSize, fit: Fit | undefined, confirmedForStepper: boolean) => {
            onSelect(selectedSize, fit, false);
          }
        : undefined,
      onCloseOverride: () => setModal(originalModalState),
    });
  };

  const photos = useProductAssets(product);
  const filteredMannequinPhotos = photos.mannequinPhotos.filter(({ color: photoColor, direction }) => photoColor === color && direction === Direction.Front);
  const filteredModelPhotos = photos.modelPhotos.filter(({ color: photoColor }) => color === photoColor);
  const mainPhoto = filteredModelPhotos[0] ?? filteredMannequinPhotos[0] ?? variant?.featured_image;
  const mainPhotoSrc = sizeImage(mainPhoto.src, '948x1200');

  const hasPreviousStep = !!modal?.previousState;
  const selectCopy = titleText ?? 'Select your size';

  const handleClose = () => {
    setModal({ toggleState: ModalToggleState.Closed });
    onClose?.();
  };

  const isDisabled = !size;
  const hideCta = hideCtaWhenDisabled && isDisabled;

  return (
    <React.Fragment>
      <div className={styles.hlStepper}>
        <div>
          <div className={styles.hlStepperHeader}>
            <button className={classNames(styles.hlStepperControl, styles.hlStepperControlBack)} onClick={() => hasPreviousStep && reverseModal()}>
              {hasPreviousStep && <img src={assetPathFn('sizing-tool-chevron-back.png')} alt="go to previous step" className={styles.hlStImgControl} />}
            </button>
            {isMobile && <span className={styles.mobileHeaderTitle}>{selectCopy}</span>}
            <button onClick={handleClose} className={styles.hlStepperControl}>
              <img src={assetPathFn('sizing-tool-close-button_90x90.png')} alt="close" />
            </button>
          </div>
          <div className={styles.hlStepperBody}>
            <div className={styles.hlStepperBodyLeft}>
              <ImageLoader src={mainPhotoSrc} className={styles.hlStepperProductImage} alt="featured" />
            </div>
            <div className={styles.hlStepperBodyRight}>
              {!isMobile && <span>{selectCopy}</span>}
              <ProductSizeSwatches
                product={product}
                selectionType={selectionType}
                selectionKey={selectionKey}
                onSelect={(newSize, fit, isAvailable) => {
                  onSelect?.(newSize, fit, isAvailable);
                }}
                style={{ marginTop: 10, padding: `0 20px` }}
              />
              {product.fits?.length > 0 && (
                <div className={styles.fitContainer}>
                  <ProductFitSwatches product={product} selectionType={selectionType} selectionKey={selectionKey} style={{ marginTop: 10 }} />
                </div>
              )}
              {product.sizingWarning && <div className={styles.sizingWarning}>{product.sizingWarning}</div>}
              <div className={styles.btnContainer}>
                <Button onClick={openSizingTool} text={'Sizing Help'} fullWidth={true} />
              </div>
              {!onNext && !hideCta && (
                <div className={styles.btnContainer}>
                  <ProductFormButton
                    disabled={isDisabled}
                    selectionType={selectionType}
                    selectionKey={selectionKey}
                    subContextOverride={subContext}
                    afterSubmit={afterSubmit}
                    disablePreCartModal={disablePreCartModal}
                    quantity={quantity}
                  />
                </div>
              )}
              {size && onNext && (
                <div className={styles.btnContainer}>
                  <Button buttonType="primary" text={'Next'} onClick={() => onNext(size)} fullWidth={true} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProductStepper;
